import React, { useEffect, useRef } from 'react';

interface Possession {
  possessionNumber: number;
  team: string;
  type: 'score' | 'miss' | 'turnover' | 'offensive_rebound';
  description: string;
  shotType?: number;
  points?: number;
}

interface GameTeams {
  team1: {
    id: string;
    name: string;
    color: string;
  };
  team2: {
    id: string;
    name: string;
    color: string;
  };
}

interface TeamColors {
  team1: string;
  team2: string;
}

interface DirectDOMIconManagerProps {
  possessionLog: Possession[];
  gameId: string;
  gameTeams: GameTeams | null;
  teamColors: TeamColors | null;
  containerRef: React.RefObject<SVGGElement | null>;
  maxPossessions?: number;
}

// Helper function to darken colors
const darkenColor = (color: string, opacity: number) => {
  if (!color) return '#666';
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Create icon path based on type
const createIconPath = (iconType: string, color: string) => {
  let pathData = '';
  
  switch (iconType) {
    case 'make':
      pathData = 'M10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10V12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12V10C8.20914 10 10 8.20914 10 6Z';
      break;
    case 'miss':
      pathData = 'M11 1.375L6.875 5.5L11 9.625L9.625 11L5.5 6.875L1.375 11L0 9.625L4.125 5.5L0 1.375L1.375 0L5.5 4.125L9.625 0L11 1.375Z';
      break;
    case 'turnover':
      pathData = 'M0 6L3.50006 0L7 6H0Z';
      break;
    default:
      pathData = 'M11 1.375L6.875 5.5L11 9.625L9.625 11L5.5 6.875L1.375 11L0 9.625L4.125 5.5L0 1.375L1.375 0L5.5 4.125L9.625 0L11 1.375Z';
  }
  
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', pathData);
  path.setAttribute('fill', color);
  path.style.transition = 'none';
  
  return path;
};

// Direct DOM manipulation component - never re-renders visually
const DirectDOMIconManager = React.memo<DirectDOMIconManagerProps>(({ 
  possessionLog, 
  gameId, 
  gameTeams,
  teamColors, 
  containerRef,
  maxPossessions = 20
}) => {
  const renderedPossessions = useRef(new Set<string>());
  const lastPossessionCount = useRef(0);
  const previousMostRecentByTeam = useRef<{ [key: string]: Possession }>({});
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Skip if no valid game data
    if (!gameId || gameId === 'no-game' || !teamColors || !gameTeams) {
      return;
    }
    
    // Clear everything if no possessions
    if (!possessionLog.length) {
      if (renderedPossessions.current.size > 0) {
        containerRef.current.innerHTML = '';
        renderedPossessions.current.clear();
        lastPossessionCount.current = 0;
        previousMostRecentByTeam.current = {};
      }
      return;
    }
    
    // Skip if no new possessions
    if (possessionLog.length === lastPossessionCount.current) {
      return;
    }
    
    const recentPossessions = possessionLog.slice(-maxPossessions);
    
    // Find the most recent possession for each team
    const mostRecentByTeam: { [key: string]: Possession } = {};
    possessionLog.forEach(possession => {
      const teamId = possession.team;
      if (!mostRecentByTeam[teamId] || possession.possessionNumber > mostRecentByTeam[teamId].possessionNumber) {
        mostRecentByTeam[teamId] = possession;
      }
    });
    
    // Store previous most recent for comparison (before async operations)
    const previousMostRecent = { ...previousMostRecentByTeam.current };
    
    import('../../utils/courtPlacement').then(({ generatePossessionPlacement }) => {
      
      if (!containerRef.current) return;
      
      // Add ONLY new possessions
      recentPossessions.forEach(possession => {
        const possessionKey = `${gameId}-${possession.possessionNumber}`;
        
        if (!renderedPossessions.current.has(possessionKey)) {
          const placement = generatePossessionPlacement(possession, gameId, gameTeams);
          
          const teamColor = placement.teamSide === 'team1' 
            ? teamColors?.team1 
            : teamColors?.team2;
          
          const teamMostRecent = mostRecentByTeam[possession.team];
          const isTeamMostRecent = teamMostRecent && possession.possessionNumber === teamMostRecent.possessionNumber;
          const opacity = isTeamMostRecent ? 1.0 : 0.5;
          const finalColor = darkenColor(teamColor || '#666', opacity);
          
          // Create DOM elements directly in the SVG
          const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          group.setAttribute('transform', `translate(${placement.x}, ${placement.y})`);
          group.setAttribute('data-possession-key', possessionKey);
          group.setAttribute('data-team', possession.team);
          
          const iconGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          if (placement.icon === 'turnover') {
            iconGroup.setAttribute('transform', `translate(-4, -3.5)`);
          } else {
            iconGroup.setAttribute('transform', `translate(-6, -6)`);
          }
          
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          if (placement.icon === 'turnover') {
            svg.setAttribute('width', '8');
            svg.setAttribute('height', '7');
          } else {
            svg.setAttribute('width', '12');
            svg.setAttribute('height', '12');
          }
          svg.setAttribute('viewBox', placement.icon === 'make' ? '0 0 12 12' : (placement.icon === 'turnover' ? '0 0 7 6' : '0 0 11 11'));
          
          const path = createIconPath(placement.icon, finalColor);
          svg.appendChild(path);
          iconGroup.appendChild(svg);
          group.appendChild(iconGroup);
          
          // Add title
          const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          title.textContent = `Possession ${possession.possessionNumber}: ${possession.type}`;
          group.appendChild(title);
          
          // Add to DOM
          if (containerRef.current) {
            containerRef.current.appendChild(group);
            renderedPossessions.current.add(possessionKey);
          }
        }
      });
      
      // Only update icons when "most recent" status actually changes
      // Compare current vs previous to identify which icons need updating
      const iconsToUpdate: Array<{ key: string; teamId: string; isMostRecent: boolean }> = [];
      
      Object.keys(mostRecentByTeam).forEach(teamId => {
        const currentMostRecent = mostRecentByTeam[teamId];
        const prevMostRecent = previousMostRecent[teamId];
        
        // If the most recent possession for this team changed
        if (!prevMostRecent || currentMostRecent.possessionNumber !== prevMostRecent.possessionNumber) {
          // Update the old most recent icon (dim it)
          if (prevMostRecent) {
            const oldKey = `${gameId}-${prevMostRecent.possessionNumber}`;
            if (renderedPossessions.current.has(oldKey)) {
              iconsToUpdate.push({
                key: oldKey,
                teamId: teamId,
                isMostRecent: false
              });
            }
          }
          
          // Update the new most recent icon (brighten it)
          const newKey = `${gameId}-${currentMostRecent.possessionNumber}`;
          if (renderedPossessions.current.has(newKey)) {
            iconsToUpdate.push({
              key: newKey,
              teamId: teamId,
              isMostRecent: true
            });
          }
        }
      });
      
      // Batch DOM updates using requestAnimationFrame to prevent flashing
      if (iconsToUpdate.length > 0) {
        requestAnimationFrame(() => {
          if (!containerRef.current) return;
          
          iconsToUpdate.forEach(({ key, teamId, isMostRecent }) => {
            const element = containerRef.current!.querySelector(`[data-possession-key="${key}"]`);
            if (element) {
              const teamColor = teamId === gameTeams!.team1.id 
                ? teamColors?.team1 
                : teamColors?.team2;
              
              const opacity = isMostRecent ? 1.0 : 0.5;
              const finalColor = darkenColor(teamColor || '#666', opacity);
              
              const path = element.querySelector('path');
              if (path && path.getAttribute('fill') !== finalColor) {
                path.setAttribute('fill', finalColor);
              }
            }
          });
        });
      }
      
      // Update the previous most recent tracking (after DOM updates)
      previousMostRecentByTeam.current = { ...mostRecentByTeam };
      
      // Remove old possessions
      const recentKeys = new Set(recentPossessions.map(p => `${gameId}-${p.possessionNumber}`));
      const toRemove: string[] = [];
      
      renderedPossessions.current.forEach(possessionKey => {
        if (!recentKeys.has(possessionKey)) {
          toRemove.push(possessionKey);
        }
      });
      
      toRemove.forEach(possessionKey => {
        // Check container still exists before querying
        if (containerRef.current) {
          const element = containerRef.current.querySelector(`[data-possession-key="${possessionKey}"]`);
          if (element) {
            element.remove();
            renderedPossessions.current.delete(possessionKey);
          }
        }
      });
      
      lastPossessionCount.current = possessionLog.length;
    });
    
  }, [possessionLog.length, gameId]);
  
  // Reset when game changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      renderedPossessions.current.clear();
      lastPossessionCount.current = 0;
      previousMostRecentByTeam.current = {};
    }
  }, [gameId, containerRef]);
  
  return null; // This component doesn't render anything directly
}, (prevProps, nextProps) => {
  // Only re-render if critical props change
  return prevProps.possessionLog.length === nextProps.possessionLog.length &&
         prevProps.gameId === nextProps.gameId &&
         prevProps.teamColors?.team1 === nextProps.teamColors?.team1 &&
         prevProps.teamColors?.team2 === nextProps.teamColors?.team2;
});

DirectDOMIconManager.displayName = 'DirectDOMIconManager';

export { DirectDOMIconManager };

// Keep the original PossessionIcon for backward compatibility if needed
interface PossessionIconProps {
  placement: {
    x: number;
    y: number;
    icon: string;
    type: string;
    possessionNumber: number;
    opacity?: number;
  };
  teamColor: string;
  index: number;
  showAnimation?: boolean;
}

const PossessionIcon = React.memo<PossessionIconProps>(({ placement, teamColor, index, showAnimation = false }) => {
  const { x, y, icon, type, possessionNumber, opacity = 1.0 } = placement;
  const finalColor = darkenColor(teamColor, opacity);
  
  return (
    <g 
      transform={`translate(${x}, ${y})`}
      style={{ transition: 'none' }}
    >
      <g transform={icon === 'turnover' ? `translate(-4, -3.5)` : `translate(-6, -6)`}>
        <svg width={icon === 'turnover' ? '8' : '12'} height={icon === 'turnover' ? '7' : '12'} viewBox={icon === 'make' ? '0 0 12 12' : (icon === 'turnover' ? '0 0 7 6' : '0 0 11 11')}>
          <path d={icon === 'make' ? 'M10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10V12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6C12 9.31371 9.31371 12 6 12V10C8.20914 10 10 8.20914 10 6Z' : 
                   icon === 'miss' ? 'M11 1.375L6.875 5.5L11 9.625L9.625 11L5.5 6.875L1.375 11L0 9.625L4.125 5.5L0 1.375L1.375 0L5.5 4.125L9.625 0L11 1.375Z' :
                   icon === 'turnover' ? 'M0 6L3.50006 0L7 6H0Z' :
                   'M11 1.375L6.875 5.5L11 9.625L9.625 11L5.5 6.875L1.375 11L0 9.625L4.125 5.5L0 1.375L1.375 0L5.5 4.125L9.625 0L11 1.375Z'} 
                fill={finalColor} />
        </svg>
      </g>
      <title>Possession {possessionNumber}: {type}</title>
    </g>
  );
});

PossessionIcon.displayName = 'PossessionIcon';

export default PossessionIcon; 